/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { RouteComponent, Router, createBrowserHistory } from 'react-resource-router';
import AppProvider from '@atlaskit/app-provider';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { Root, Main } from '@atlaskit/navigation-system';
import { FlagsProvider } from '@atlaskit/flag';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { routes } from './routes';
import { useReactUFORouteWatcher } from '../analytics/react-ufo/useReactUFORouteWatcher';

import { initAnalyticsClient, type LoggedInUser } from '@atlassian/twg-microsite-experience';
import { LocaleProvider } from '@atlassian/twg-microsite-experience/locale-provider';
import { NavigationBar } from '@atlassian/twg-microsite-experience/NavigationBar';
import { UserProvider } from '@atlassian/twg-microsite-experience/user-provider';
import { AnalyticsProvider } from '@atlassian/twg-microsite-experience/analytics-provider';
import { PanelProvider } from '@atlassian/twg-microsite-experience/panel-provider';
import { useCallback, useMemo } from 'react';
import { initializeFeatureGates } from '../feature-flags';
import { DebugPanel } from '@atlassian/twg-microsite-experience/DebugPanel';

// The microsite only registers a single route (`/`), so any other pathname
// (e.g. `/hello`) is rewritten to `/` before the Router subscribes — this
// avoids an extra render pass or loading-state flicker on first mount.
const history = createBrowserHistory();
if (history.location.pathname !== '/') {
	history.replace({ pathname: '/', search: history.location.search, hash: history.location.hash });
}

// Sibling of `<Root>` so it can subscribe to the `Router` context. The hook
// is a no-op on first render (the initial page-load is traced by `initUFO`)
// and fires `traceUFOTransition` on subsequent route name changes.
const RouteWatcher = (): null => {
	useReactUFORouteWatcher();
	return null;
};

export const App = (): JSX.Element => {
	const analyticsClient = useMemo(() => initAnalyticsClient(), []);

	const onUserLoginComplete = useCallback(async (user: LoggedInUser) => {
		await initializeFeatureGates(user.accountId, user.orgId);
	}, []);

	return (
		<UFOSegment name="twg-microsite-root">
			<RelayEnvironmentProvider>
				<AnalyticsProvider analyticsClient={analyticsClient}>
					<AppProvider defaultColorMode="dark">
						<FlagsProvider>
							{/* Any component or provider that requires a FG to be initialized must be wrapped in UserProvider.
							    This also means there should be NO FGs being resolved outside of UserProvider */}
							<UserProvider onUserLoginComplete={onUserLoginComplete}>
								<LocaleProvider>
									<PanelProvider>
										<Router history={history} routes={routes}>
											<RouteWatcher />
											<Root>
												<Main>
													<NavigationBar />
													<RouteComponent />
												</Main>
												<DebugPanel />
											</Root>
										</Router>
									</PanelProvider>
								</LocaleProvider>
							</UserProvider>
						</FlagsProvider>
					</AppProvider>
				</AnalyticsProvider>
			</RelayEnvironmentProvider>
		</UFOSegment>
	);
};
